import { useState } from "react";
import { View } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import {
  isTicketActive,
  TICKET_STATUS_LABELS,
  TICKET_TYPE_LABELS,
} from "@brad-tipper/app-frontend/tickets";

import { Badge, Button, Card, Input, Screen, Text, useTheme } from "~/components/ui";
import {
  useAddTicketNote,
  useSetTicketStatus,
  useTicket,
} from "~/hooks/useTickets";
import { spacing } from "~/styles/tokens";

export default function TicketScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const { id } = useLocalSearchParams<{ id: string }>();
  const ticket = useTicket(id);
  const addNote = useAddTicketNote(id ?? "");
  const setStatus = useSetTicketStatus(id ?? "");
  const [note, setNote] = useState("");

  const data = ticket.data;
  const active = data ? isTicketActive(data.status) : true;

  async function send() {
    const body = note.trim();
    if (!body) return;
    await addNote.mutateAsync(body);
    setNote("");
  }

  return (
    <Screen scroll>
      <Button
        label="Support"
        variant="ghost"
        size="sm"
        onPress={() => router.replace("/support" as never)}
      />
      {!data ? (
        <Text variant="body" color={ticket.error ? colors.danger : colors.muted}>
          {ticket.error ? "Unable to load this ticket." : "Loading…"}
        </Text>
      ) : (
        <>
          <Text variant="heading">{`#${data.number} ${data.title}`}</Text>
          <Text variant="caption" color={colors.muted}>
            {TICKET_TYPE_LABELS[data.type]} · opened{" "}
            {new Date(data.createdAt).toLocaleDateString()}
          </Text>
          <Card elevation="raised">
            <View style={{ gap: spacing.sm }}>
              <View style={{ flexDirection: "row", gap: spacing.sm }}>
                <Badge
                  label={TICKET_STATUS_LABELS[data.status]}
                  variant={active ? "warning" : "default"}
                />
                {data.awaitingSupport ? (
                  <Text variant="caption" color={colors.muted}>
                    Waiting for our reply
                  </Text>
                ) : null}
              </View>
              <Text variant="body">{data.description}</Text>
            </View>
          </Card>

          <Text variant="label">Conversation</Text>
          {data.messages.length === 0 ? (
            <Text variant="body" color={colors.muted}>
              No notes yet. We will email you when we reply.
            </Text>
          ) : (
            data.messages.map((message) => (
              <Card key={message.id} elevation="raised">
                <View style={{ gap: spacing.xs }}>
                  <Text variant="caption" color={colors.muted}>
                    {message.from === "support" ? "Support" : "You"} ·{" "}
                    {new Date(message.createdAt).toLocaleString()}
                  </Text>
                  <Text variant="body">{message.body}</Text>
                </View>
              </Card>
            ))
          )}

          <Card elevation="raised">
            <View style={{ gap: spacing.md }}>
              <Text variant="label">
                {active ? "Add a note" : "Add a note (reopens this ticket)"}
              </Text>
              <Input
                label="Note"
                value={note}
                onChangeText={setNote}
                multiline
                numberOfLines={4}
                placeholder="Anything else we should know?"
              />
              {addNote.error ? (
                <Text variant="caption" color={colors.danger}>
                  {addNote.error instanceof Error
                    ? addNote.error.message
                    : "Unable to send."}
                </Text>
              ) : null}
              <Button
                label="Send note"
                loading={addNote.isPending}
                disabled={!note.trim()}
                onPress={() => void send()}
                fullWidth
              />
              <Button
                label={active ? "Close ticket" : "Reopen"}
                variant="secondary"
                disabled={setStatus.isPending}
                onPress={() =>
                  void setStatus.mutateAsync(active ? "closed" : "open")
                }
                fullWidth
              />
            </View>
          </Card>
        </>
      )}
    </Screen>
  );
}
